import { useRef } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { Button, Text, Icon, theme, styles as baseStyles } from '@socrait/react';
import FlashMessage from 'react-native-flash-message';

import { ErrorBox } from '~/components/ErrorBox';
import { FlashBanner } from '~/components/FlashBanner';
import { LayoutWithSidebar } from '~/layouts/LayoutWithSidebar';
import { Period } from '~/services/types';
import { getConciseDateTime } from '~/utils/date';

type ArchivedClassLayoutProps = {
  period: Period | null;
  onClose: () => void;
  onRestore: (period: Period, ref?: React.RefObject<FlashMessage>) => void;
  isRestoring?: boolean;
  children: React.ReactNode;
};

export function ArchivedClassLayout({
  period,
  onClose,
  onRestore,
  children,
  isRestoring = false,
}: ArchivedClassLayoutProps) {
  const notificationRef = useRef<FlashMessage>(null);

  return (
    <View>
      <LayoutWithSidebar isSimple={true}>
        <View style={styles.container}>
          <View style={styles.header}>
            {!!period && (
              <View style={styles.classInfo}>
                <View style={styles.shortName}>
                  <Text>{period.shortName}</Text>
                </View>
                <View style={styles.title}>
                  <Text style={baseStyles.boldText}>{period.name}</Text>
                  {!!period.archivedAt && (
                    <View style={styles.archivedAt}>
                      <Icon type="archive" height={18} />
                      <Text style={styles.archivedAtText}>{getConciseDateTime(new Date(period.archivedAt))}</Text>
                    </View>
                  )}
                </View>
              </View>
            )}
            <View style={styles.headerButtons}>
              {!!period && (
                <Button name="Restore" onPress={() => onRestore(period, notificationRef)} loading={isRestoring} />
              )}
              <Pressable onPress={onClose}>
                <Icon type="close" />
              </Pressable>
            </View>
          </View>
          {!!period ? (
            <>{children}</>
          ) : (
            <ErrorBox
              text="Could not load period data. Please try refreshing the page."
              containerStyle={{ borderRadius: 0 }}
            />
          )}
        </View>
      </LayoutWithSidebar>
      <FlashMessage
        ref={notificationRef}
        position="top"
        animationDuration={100}
        duration={10000}
        MessageComponent={({ message }: { message: { message: string; type: 'warning' | 'success' } }) => (
          <FlashBanner message={message} />
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  header: {
    alignItems: 'center',
    borderBottomColor: theme.colors.gray,
    borderBottomWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.xlarge * 2,
    paddingVertical: theme.spacing.xxlarge,
  },
  classInfo: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xlarge,
  },
  shortName: {
    backgroundColor: theme.colors.gray,
    borderRadius: theme.borderRadius.xlarge * 2,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: 50,
    justifyContent: 'center',
    width: 50,
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.medium,
  },
  archivedAt: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xsmall,
  },
  archivedAtText: {
    fontSize: theme.fontSizes.small,
  },
  headerButtons: {
    alignSelf: 'flex-end',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xlarge * 2,
    marginLeft: 'auto',
  },
});
